import React from 'react';
import { StyleSheet, View, Text, ScrollView, TouchableOpacity } from 'react-native';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { MainStackParamList } from '../../navigation/MainNavigator';
import { spacing, typography } from '../../constants/theme';
import { Ionicons } from '@expo/vector-icons';
import { useTheme } from '../../context/ThemeContext';
import { appConfig } from '../../config/appConfig';

type Props = NativeStackScreenProps<MainStackParamList, 'TermsConditions'>;

const TermsConditionsScreen = ({ navigation }: Props) => {
  const { theme } = useTheme();
  
  return (
    <View style={[styles.container, { backgroundColor: theme.colors.background }]}>
      <View style={styles.header}>
        <TouchableOpacity 
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color={theme.colors.text} />
        </TouchableOpacity>
        <Text style={[styles.headerTitle, { color: theme.colors.text }]}>
          Términos y Condiciones
        </Text>
        <View style={styles.placeholder} />
      </View>
      
      <ScrollView style={styles.scrollView}>
        <View style={styles.content}>
          <Text style={[styles.title, { color: theme.colors.text }]}>
            Términos y Condiciones de {appConfig.name}
          </Text>
          
          <Text style={[styles.updateDate, { color: theme.colors.grey.medium }]}>
            Última actualización: 20 de abril de 2025
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Por favor, lea estos términos y condiciones cuidadosamente antes de utilizar nuestra aplicación.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            1. Aceptación de los Términos
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Al acceder o utilizar la aplicación {appConfig.name}, usted acepta estar sujeto a estos Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, no podrá acceder a la aplicación.
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            <Text style={{ fontWeight: 'bold' }}>Aceptación Explícita: </Text>
            Su uso continuado de la aplicación constituye su aceptación explícita de estos términos y condiciones en su totalidad. Si no está de acuerdo con estos términos, debe dejar de usar la aplicación inmediatamente y eliminarla de su dispositivo.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            2. Restricción de Edad
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Esta aplicación está destinada exclusivamente para personas mayores de 16 años. Al utilizar {appConfig.name}, usted declara y garantiza que tiene al menos 16 años de edad.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            3. Descargo de Responsabilidad sobre Rutinas y Ejercicios
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            {appConfig.name} no se hace responsable de las rutinas de ejercicio que se puedan encontrar en la aplicación. No nos responsabilizamos por los ejercicios que se puedan ver en la aplicación ni proporcionamos instrucciones sobre cómo realizarlos correctamente.
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Es su responsabilidad consultar con un profesional de la salud antes de comenzar cualquier programa de ejercicios. La aplicación no pretende reemplazar el consejo, diagnóstico o tratamiento médico profesional.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            4. Contenido Generado por Usuarios
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            No nos hacemos responsables de nombres ofensivos o contenido inapropiado que pueda aparecer dentro de la aplicación como resultado de contenido generado por usuarios. Aunque nos esforzamos por mantener un entorno respetuoso, no podemos moderar todo el contenido en tiempo real.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            5. Seguridad de Datos
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            {appConfig.name} no se hace responsable en caso de pérdida parcial o total de los datos almacenados en la aplicación. Aunque implementamos medidas de seguridad para proteger sus datos, no podemos garantizar su integridad o disponibilidad permanente.
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Se recomienda a los usuarios mantener copias de seguridad de cualquier información importante que almacenen en la aplicación.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            6. Modificaciones a los Términos
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigor inmediatamente después de su publicación en la aplicación. Su uso continuado de la aplicación después de cualquier cambio constituye su aceptación de los nuevos términos.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            7. Privacidad
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Su privacidad es importante para nosotros. Nuestra Política de Privacidad explica cómo recopilamos, usamos y protegemos la información que usted nos proporciona. Al utilizar nuestra aplicación, usted acepta las prácticas descritas en nuestra Política de Privacidad.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            8. Terminación
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Podemos terminar o suspender su acceso a la aplicación inmediatamente, sin previo aviso, por cualquier motivo, incluyendo, sin limitación, si usted viola estos Términos y Condiciones.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            9. Ley Aplicable y Renuncia a Acciones Legales
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Estos términos se regirán e interpretarán de acuerdo con las leyes del país de residencia, sin tener en cuenta sus disposiciones sobre conflictos de leyes.
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            <Text style={{ fontWeight: 'bold' }}>Renuncia a Acciones Legales: </Text>
            Al utilizar esta aplicación, usted acepta renunciar expresamente a su derecho de iniciar o participar en cualquier acción legal, demanda colectiva o procedimiento judicial en contra de {appConfig.name}, sus propietarios, desarrolladores, o cualquier persona relacionada con la aplicación. Cualquier disputa será resuelta mediante arbitraje vinculante de manera individual.
          </Text>
          
          <Text style={[styles.sectionTitle, { color: theme.colors.text }]}>
            10. Contacto
          </Text>
          
          <Text style={[styles.paragraph, { color: theme.colors.text }]}>
            Si tiene alguna pregunta sobre estos Términos y Condiciones, puede contactarnos a través de la información proporcionada en la aplicación.
          </Text>
          
          <View style={styles.footer}>
            <Text style={[styles.footerText, { color: theme.colors.grey.medium }]}>
              Desarrollado por: {appConfig.author}
            </Text>
            <Text style={[styles.footerText, { color: theme.colors.grey.medium }]}>
              {appConfig.copyrightText}
            </Text>
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: spacing.md,
    paddingVertical: spacing.md,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0, 0, 0, 0.1)',
  },
  headerTitle: {
    fontSize: typography.fontSize.lg,
    fontWeight: 'bold',
  },
  backButton: {
    padding: spacing.sm,
  },
  placeholder: {
    width: 24,
  },
  scrollView: {
    flex: 1,
  },
  content: {
    padding: spacing.lg,
    paddingBottom: spacing.xxl,
  },
  title: {
    fontSize: typography.fontSize.xl,
    fontWeight: 'bold',
    marginBottom: spacing.sm,
    textAlign: 'center',
  },
  updateDate: {
    fontSize: typography.fontSize.sm,
    marginBottom: spacing.xl,
    textAlign: 'center',
  },
  sectionTitle: {
    fontSize: typography.fontSize.md,
    fontWeight: 'bold',
    marginTop: spacing.lg,
    marginBottom: spacing.sm,
  },
  paragraph: {
    fontSize: typography.fontSize.md,
    lineHeight: 22,
    marginBottom: spacing.md,
  },
  footer: {
    marginTop: spacing.xl,
    paddingTop: spacing.lg,
    borderTopWidth: 1,
    borderTopColor: 'rgba(0, 0, 0, 0.1)',
  },
  footerText: {
    fontSize: typography.fontSize.sm,
    textAlign: 'center',
    marginBottom: spacing.xs,
  },
});

export default TermsConditionsScreen; 